import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Search, Package, Truck, CheckCircle, Clock, ArrowLeft, AlertCircle } from 'lucide-react';
import { trackOrder } from '../lib/api';

const statusSteps = ['pending', 'paid', 'shipped', 'delivered'];
const statusIcons = { pending: Clock, paid: CheckCircle, shipped: Truck, delivered: Package };
const statusLabels = { pending: 'Order Placed', paid: 'Payment Confirmed', shipped: 'Shipped', delivered: 'Delivered' };

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleTrack = async (e) => {
    e.preventDefault();
    setError('');
    setOrder(null);
    setLoading(true);
    try {
      const res = await trackOrder(orderNumber.trim(), email.trim());
      setOrder(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found. Please check your order number and email.');
    } finally {
      setLoading(false);
    }
  };

  const currentStep = order ? statusSteps.indexOf(order.status) : -1;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="track-order-page">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/images/coras-logo.png" alt="Cora's Naturals" className="h-12" />
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft size={14} className="mr-1" /> Back to Shop
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <Card className="shadow-lg border-0">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl" style={{ fontFamily: "'Playfair Display', 'Georgia', serif" }}>Track Your Order</CardTitle>
            <CardDescription>Enter your order number and email to check status</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleTrack} className="space-y-4 max-w-md mx-auto">
              {error && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg text-sm" data-testid="track-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label>Order Number</Label>
                <Input
                  placeholder="e.g., ORD-A1B2C3D4"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  required
                  data-testid="track-order-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="track-email"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-full"
                style={{ backgroundColor: '#4A5D3A' }}
                disabled={loading}
                data-testid="track-submit"
              >
                <Search size={14} className="mr-2" />
                {loading ? 'Searching...' : 'Track Order'}
              </Button>
            </form>

            {order && (
              <div className="mt-10 border-t pt-8" data-testid="order-result">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="font-semibold text-lg">{order.order_number}</h3>
                    <p className="text-sm text-gray-500">{order.customer_name}</p>
                  </div>
                  <Badge className="text-sm px-3 py-1">{order.status?.toUpperCase()}</Badge>
                </div>

                {/* Status Timeline */}
                <div className="flex items-center justify-between mb-8">
                  {statusSteps.map((step, i) => {
                    const Icon = statusIcons[step];
                    const isActive = i <= currentStep;
                    const isCurrent = i === currentStep;
                    return (
                      <div key={step} className="flex flex-col items-center flex-1 relative">
                        {i > 0 && (
                          <div className={`absolute top-5 right-1/2 w-full h-0.5 -translate-y-1/2 ${i <= currentStep ? 'bg-[#4A5D3A]' : 'bg-gray-200'}`} />
                        )}
                        <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center ${isActive ? 'bg-[#4A5D3A] text-white' : 'bg-gray-200 text-gray-400'} ${isCurrent ? 'ring-4 ring-[#4A5D3A]/20' : ''}`}>
                          <Icon size={18} />
                        </div>
                        <span className={`text-xs mt-2 ${isActive ? 'text-[#4A5D3A] font-medium' : 'text-gray-400'}`}>
                          {statusLabels[step]}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Order Items */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Order Items</h4>
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex justify-between py-2 border-b last:border-0">
                      <span>{item.product_name} x{item.quantity}</span>
                      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-3 font-bold">
                    <span>Total</span>
                    <span>${order.total_amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
